'use strict';

import Swiper from 'swiper';
import { FreeMode, Mousewheel, Navigation, Pagination, Keyboard } from 'swiper/modules';

const SELECTOR = '.js-gallery';

export function init() {
    const elements = document.querySelectorAll(SELECTOR);

    if (!elements.length) {
        return;
    }

    elements.forEach(element => {
        const slidesContainerEl = element.querySelector('.image-gallery__content');

        const fixedBodyClass = element.dataset.galleryFixedBodyClass || 'image-gallery-fixed-body';
        const fullscreenClass = element.dataset.galleryFullscreenClass || 'image-gallery--fullscreen';

        const detailsContainer = element.querySelector(
            element.dataset.galleryDetailsContainerSelector || '.image-gallery__details'
        );

        const positionContainer = element.querySelector(
            element.dataset.galleryPositionContainerSelector || '.image-gallery__position'
        );

        const prevButton = element.querySelector(
            element.dataset.galleryPrevButtonSelector || '.image-gallery__button--previous'
        );

        const nextButton = element.querySelector(
            element.dataset.galleryNextButtonSelector || '.image-gallery__button--next'
        );

        const fullscreenButton = element.querySelector(
            element.dataset.galleryFullscreenButtonSelector || '.image-gallery__button--fullscreen'
        );

        const images = element.querySelectorAll(
            element.dataset.galleryImageSelector || '.gallery-slide__image'
        );

        const indexContainer = element.querySelector('.image-gallery__index');
        const totalSlides = element.querySelectorAll('.image-gallery__slide').length;
        const isSingleSlide = totalSlides === 1;

        if (isSingleSlide) {
            prevButton?.classList.add('disabled');
            nextButton?.classList.add('disabled');
        }

        const swiper = new Swiper(slidesContainerEl, {
            init: false,
            modules: [
                FreeMode,
                Mousewheel,
                Navigation,
                Pagination,
                Keyboard
            ],
            slidesPerView: 'auto',
            simulateTouch: false,
            freeMode: { enabled: true, sticky: true },
            mousewheel: { releaseOnEdges: true, forceToAxis: true, sensitivity: 0.8 },
            navigation: { prevEl: prevButton, nextEl: nextButton },
            pagination: { el: positionContainer, type: 'fraction' },
            observer: true,
            observeParents: true,
            observeSlideChildren: true,
            resizeObserver: true,
        });

        /* --------------------------------------------------
        Init + Events
        -------------------------------------------------- */

        swiper.on('init', () => {
            updateMetaFromIndex();
            updateIndex();
            updateNavigationButtons();
            adjustTailOffsetAndUpdate();
        });

        swiper.on('activeIndexChange', () => {
            updateMetaFromIndex();
            updateIndex();
            updateNavigationButtons();
        });

        swiper.on('slideChangeTransitionEnd', () => {
            updateMetaFromIndex();
            updateIndex();
            updateNavigationButtons();
            adjustTailOffsetAndUpdate();
        });

        swiper.init();

        /* --------------------------------------------------
        Fullscreen Controls
        -------------------------------------------------- */
        if (fullscreenButton) {
            fullscreenButton.addEventListener('click', () => {
                if (isGalleryFullscreen()) {
                    endFullscreen();
                } else {
                    startFullscreen();
                }
            }, false);
        }

        document.addEventListener('keyup', event => {
            if (event.defaultPrevented) {
                return;
            }

            if (isGalleryFullscreen() === false) {
                return;
            }

            switch (event.key || event.code) {
                case 'Escape':
                case 'Esc':
                    endFullscreen();
                    break;
                case 'ArrowLeft':
                case 'Left':
                    swiper.slidePrev();
                    break;
                case 'ArrowRight':
                case 'Right':
                    swiper.slideNext();
                    break;
                default:
                    return;
            }
        });

        /* --------------------------------------------------
        Helpers
        -------------------------------------------------- */

        function updateMetaFromIndex() {
            if (!detailsContainer) return;
            const i =
            typeof swiper.realIndex === 'number' ? swiper.realIndex : swiper.activeIndex;
            const slideEl = swiper.slides[i];
            if (!slideEl) return;
            const contentEl = slideEl.querySelector('.gallery-slide__content');
            detailsContainer.innerHTML = contentEl ? contentEl.innerHTML : '';
        }

        function updateNavigationButtons() {
            if (!prevButton || !nextButton) return;

            const i =
            typeof swiper.realIndex === 'number' ? swiper.realIndex : swiper.activeIndex;

            if (i > 0) prevButton.classList.remove('disabled');
            else prevButton.classList.add('disabled');

            if (i < totalSlides - 1) nextButton.classList.remove('disabled');
            else nextButton.classList.add('disabled');
        }

        function updateIndex() {
            if (!indexContainer) return;
            const current =
            typeof swiper.realIndex === 'number'
            ? swiper.realIndex + 1
            : swiper.activeIndex + 1;
            indexContainer.textContent = `${current} / ${totalSlides}`;
            indexContainer.setAttribute('aria-label', `Image ${current} of ${totalSlides}`);
        }

        function adjustTailOffsetAndUpdate() {
            const container = slidesContainerEl;
            const swiperWidth = container.clientWidth;
            const lastSlide = swiper.slides[swiper.slides.length - 1];
            const lastSlideWidth = lastSlide
            ? Math.round(lastSlide.getBoundingClientRect().width)
            : 0;

            const needed = Math.max(0, swiperWidth - lastSlideWidth);

            if (swiper.params.slidesOffsetAfter !== needed) {
                swiper.params.slidesOffsetAfter = needed;
                swiper.update();
                updateNavigationButtons();
                updateIndex();
            }
        }

        function isGalleryFullscreen() {
            return element.classList.contains(fullscreenClass);
        }

        let documentScrollTop = 0;

        function trapFocus(event) {
            const focusableElements = Array.from(
                element.querySelectorAll('button, a, input, [tabindex="0"]')
            ).filter(el => !el.classList.contains('disabled'));
            const first = focusableElements[0];
            const last = focusableElements[focusableElements.length - 1];
            if (event.key === 'Tab') {
                if (event.shiftKey) {
                    if (document.activeElement === first) {
                        last?.focus();
                        event.preventDefault();
                    }
                } else if (document.activeElement === last) {
                    first?.focus();
                    event.preventDefault();
                }
            }
        }

        function startFullscreen() {
            switchImages();
            element.classList.add(fullscreenClass);
            documentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
            document.body.classList.add(fixedBodyClass);
            document.querySelector('.image-gallery__fullscreen-close')?.focus();
            document.body.style.overflow = 'hidden';
            document.addEventListener('keydown', trapFocus);
        }

        function endFullscreen() {
            switchImages();
            element.classList.remove(fullscreenClass);
            document.body.classList.remove(fixedBodyClass);
            document.documentElement.scrollTop = document.body.scrollTop = documentScrollTop;
            document.body.style.overflow = '';
            document.removeEventListener('keydown', trapFocus);
        }

        function switchImages() {
            images.forEach(image => {
                const { fullscreenSrc } = image.dataset;

                if (!fullscreenSrc) {
                    return;
                }

                image.dataset.fullscreenSrc = image.src;
                image.src = fullscreenSrc;
            });
        }

        /* --------------------------------------------------
        Image-load updates (important for width recalcs)
        -------------------------------------------------- */

        const updateAfterLoad = () => {
            swiper.update();
            adjustTailOffsetAndUpdate();
            updateNavigationButtons();
            updateIndex();
        };

        images.forEach(img => {
            if (img.complete) return;
            img.addEventListener('load', updateAfterLoad, { once: true });
        });

        Promise.all(
            [...images].map(img =>
                img.decode ? img.decode().catch(() => {}) : Promise.resolve()
            )
        ).then(updateAfterLoad);

        window.addEventListener('resize', () => {
            swiper.update();
            adjustTailOffsetAndUpdate();
            updateNavigationButtons();
        });
    });
}
